import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { Button, Card, Chip, Dialog, EmptyState, Skeleton, toast } from '@/ui';
import { useSessionStore } from '@/auth/session.store';
import { maskEmail, maskPhone, revealOrMask } from '@/lib/admin-mask';
import {
  fetchPlatformUser,
  resetUserPassword,
  setUserRole,
  setUserStatus,
  type PasswordResetResult,
} from './api';
import { adminKeys } from './query-keys';
import { invalidateAfterUserMutation } from './invalidation';
import { MEMBER_TYPE_LABEL_KEYS, ROLE_LABEL_KEYS, USER_TYPE_LABEL_KEYS } from './labels';
import { adminErrorMessage } from './error-message';
import { formatDateTime } from './format';
import styles from './Admin.module.css';

/**
 * KULLANICI DETAYI (`/yonetim/kullanicilar/:userId`) — menude YOK.
 *
 * ========================= SUNUCU KURALLARININ AYNASI ======================
 * Uc islem de o kisinin BUTUN oturumlarini etkiliyor ve sunucu ikisini
 * REDDEDIYOR:
 *
 *   kendi kaydini pasiflestirme / dusurme -> 400 CANNOT_MODIFY_SELF
 *   son etkin super_admin'i dusurme       -> 400 LAST_SUPER_ADMIN_PROTECTED
 *
 * Birincisi ISTEMCIDE DE KAPATILIYOR: kimin kim oldugu burada bilinebilir ve
 * calismayacak bir butonu aktif gostermek kullaniciyi bos yere hataya
 * surukler. Ikincisi KAPATILMIYOR: "su an kac etkin super_admin var" bilgisi
 * istemcide yok; tahmine dayali bir kilit, yanlis tahminde dogru islemi
 * engellerdi. O yuzden sunucunun reddi cevrilip gosteriliyor.
 *
 * Kural ISTEMCIDE YENIDEN UYGULANMIYOR, YANSITILIYOR: tek gercek koruma
 * sunucuda; buradaki kapali buton yalniz bir nezaket.
 *
 * ==================== PLATFORM YONETICILIGI VERILEMEZ =====================
 * (2026-08-30 urun karari) `super_admin` rolu bu ekrandan — ve API'nin HICBIR
 * ucundan — VERILEMEZ; yalnizca veritabanindan verilir
 * (resources/promote-super-admin.sql). Sunucu denemeyi 403
 * SUPER_ADMIN_NOT_ASSIGNABLE ile reddediyor, repo katmaninda da ikinci bir kapi var.
 * Gerekce: super_admin butun organizasyonlarin ustunde calisir, her parolayi
 * sifirlayabilir ve her IBAN'i tanimlayabilir — bu rolu verebilen bir uc, ele
 * gecirilen TEK bir yonetici hesabindan platformun kalici devralinmasi demektir.
 *
 * GERI ALMAK mumkun ve oyle kalmali: dusurme bir yetki YUKSELTMESI degil ve
 * LAST_SUPER_ADMIN_PROTECTED platformu yoneticisiz birakmayi zaten engelliyor.
 * Bu yuzden dugme YALNIZ hedef super_admin ise ciziliyor.
 *
 * ========================= SIFRE SIFIRLAMA ================================
 * Kendi hesabina da KAPALI — sunucu buna izin veriyor ama sonucu bir tuzak:
 * sifre degisir, `mustChangePassword` acilir, oturum bir sonraki adimda
 * /ilk-giris'e dusurulur ve gecici sifre POSTAYLA gider; yani kullanici kendi
 * ekranindan kendini kilitler.
 *
 * ============================== GIZLILIK ==================================
 * Sunucu projeksiyonunda TC KIMLIK YOK ve `user_password` hicbir zaman
 * secilmiyor. Burada gosterilenler iletisim ve uyelik bilgileri; ikisi de
 * varsayilan olarak maskeli.
 */

type PendingAction = 'deactivate' | 'activate' | 'demote' | 'passwordReset';

export function PlatformUserDetailPage() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selfUserId = useSessionStore((state) => state.snapshot?.user.id ?? '');

  const userId = params.userId ?? '';
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [revealContact, setRevealContact] = useState(false);
  const [resetResult, setResetResult] = useState<PasswordResetResult | null>(null);

  const query = useQuery({
    queryKey: adminKeys.userDetail(userId),
    queryFn: () => fetchPlatformUser(userId),
    enabled: userId !== '',
  });

  const onMutationError = (error: unknown): void => {
    toast({ message: adminErrorMessage(error, t), tone: 'danger' });
  };

  const status = useMutation({
    mutationFn: setUserStatus,
    onSuccess: async (_result, variables) => {
      toast({
        message: variables.isActive ? t('admin.users.activated') : t('admin.users.deactivated'),
        tone: 'success',
      });
      await invalidateAfterUserMutation(queryClient);
    },
    onError: onMutationError,
  });

  const role = useMutation({
    mutationFn: setUserRole,
    onSuccess: async () => {
      toast({ message: t('admin.users.roleUpdated'), tone: 'success' });
      await invalidateAfterUserMutation(queryClient);
    },
    onError: onMutationError,
  });

  const passwordReset = useMutation({
    mutationFn: resetUserPassword,
    onSuccess: async (result) => {
      // Gecici sifre YALNIZ BIR KEZ gelir; once ekrana yaziliyor, sonra
      // gecersiz kilma yapiliyor — sirasi degisirse yeniden cizim panelin
      // acilmasindan once gerceklesir ve deger gorunmeden kaybolabilir.
      setResetResult(result);
      toast({
        message: result.emailDelivered
          ? t('admin.users.passwordResetMailed')
          : t('admin.users.passwordResetNotMailed'),
        tone: result.emailDelivered ? 'success' : 'warning',
      });
      await invalidateAfterUserMutation(queryClient);
    },
    onError: onMutationError,
  });

  if (query.isPending) {
    return (
      <Card padding="lg">
        <Skeleton shape="block" height="320px" />
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          icon={<ShieldAlert size={28} aria-hidden="true" />}
          title={t('admin.users.notFoundTitle')}
          description={adminErrorMessage(query.error, t)}
          action={
            <Button
              tone="neutral"
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  const detail = query.data;
  const user = detail.user;
  // pg int8 STRING doner — String()/String() karsilastirmasi ZORUNLU.
  const isSelf = String(user.id) === String(selfUserId);
  const isSuperAdmin = detail.roleCode === 'super_admin';
  const busy = status.isPending || role.isPending || passwordReset.isPending;
  const typeKey =
    user.userType === null ? null : (USER_TYPE_LABEL_KEYS[user.userType] ?? null);

  const confirm = (): void => {
    const action = pending;
    setPending(null);
    if (!action) return;
    if (action === 'deactivate') status.mutate({ userId: user.id, isActive: false });
    else if (action === 'activate') status.mutate({ userId: user.id, isActive: true });
    else if (action === 'demote') role.mutate({ userId: user.id, userRoleCode: 'user' });
    else passwordReset.mutate({ userId: user.id });
  };

  /** Onay metni — hedefi ADIYLA ve sonucunu ACIKCA soyler. */
  const dialogCopy = (): { title: string; description: string; confirmLabel: string } => {
    const name = user.userFullName || user.userEmail;
    switch (pending) {
      case 'activate':
        return {
          title: t('admin.users.activateTitle'),
          description: t('admin.users.activateDescription', { name }),
          confirmLabel: t('admin.users.activateAction'),
        };
      case 'demote':
        return {
          title: t('admin.users.demoteTitle'),
          description: t('admin.users.demoteDescription', { name }),
          confirmLabel: t('admin.users.demoteAction'),
        };
      case 'passwordReset':
        return {
          title: t('admin.users.passwordResetTitle'),
          description: t('admin.users.passwordResetDescription', { name }),
          confirmLabel: t('admin.users.passwordResetAction'),
        };
      default:
        return {
          title: t('admin.users.deactivateTitle'),
          description: t('admin.users.deactivateDescription', { name }),
          confirmLabel: t('admin.users.deactivateAction'),
        };
    }
  };

  const copy = dialogCopy();

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.pageHead}>
          <div className={styles.rowMain}>
            <h1 className={styles.title}>{user.userFullName || t('admin.users.unnamed')}</h1>
            <span className={styles.muted}>
              {revealOrMask(user.userEmail, revealContact, maskEmail)}
            </span>
          </div>
          <div className={styles.badges}>
            {isSelf ? <Chip tone="info">{t('members.you')}</Chip> : null}
            {typeKey === null ? null : <Chip tone="neutral">{t(typeKey)}</Chip>}
            <Chip tone={isSuperAdmin ? 'warning' : 'neutral'}>
              {t(ROLE_LABEL_KEYS[detail.roleCode] ?? '')}
            </Chip>
            <Chip tone={user.isActive ? 'success' : 'danger'}>
              {user.isActive ? t('admin.status.active') : t('admin.status.inactive')}
            </Chip>
          </div>
        </div>
        <label className={styles.revealToggle}>
          <input
            type="checkbox"
            checked={revealContact}
            onChange={(event) => {
              setRevealContact(event.target.checked);
            }}
          />
          {t('admin.revealContact')}
        </label>
      </Card>

      {/* Gecici sifre paneli: sunucu bu degeri BIR DAHA vermez. Kapatilabilir
          bir bildirimde degil, kalici bir panelde ve elden teslim uyarisiyla. */}
      {resetResult?.tempPassword ? (
        <Card padding="md">
          <h2 className={styles.sectionTitle}>{t('admin.users.tempPasswordTitle')}</h2>
          <p className={styles.warningNotice}>{t('admin.users.tempPasswordNotice')}</p>
          <code className={styles.tempPassword}>{resetResult.tempPassword}</code>
        </Card>
      ) : null}

      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.users.contactSection')}</h2>
        <div className={styles.detailGrid}>
          <Field label={t('fullName')} value={user.userFullName} />
          <Field label={t('email')} value={revealOrMask(user.userEmail, revealContact, maskEmail)} />
          <Field
            label={t('admin.users.phone')}
            value={revealOrMask(user.userPhone, revealContact, maskPhone)}
          />
          <Field label={t('admin.users.city')} value={user.userCity} />
          <Field label={t('admin.users.district')} value={user.userDistrict} />
          <Field label={t('admin.users.createdAt')} value={formatDateTime(user.createdAt)} />
          <Field
            label={t('admin.users.accountCount')}
            value={`${String(detail.activeAccountCount)} / ${String(detail.accountCount)}`}
          />
        </div>
        {/* Projeksiyon dar tutuluyor: kimlik numarasi sunucudan HIC gelmiyor. */}
        <p className={styles.muted}>{t('admin.users.minimalProjectionNote')}</p>
      </Card>

      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.users.membershipSection')}</h2>
        {detail.organization === null ? (
          <p className={styles.muted}>{t('admin.users.noOrganization')}</p>
        ) : (
          <>
            <div className={styles.detailGrid}>
              <Field
                label={t('nav.organization')}
                value={detail.organization.organizationName}
              />
              <Field
                label={t('admin.users.memberType')}
                value={
                  detail.organization.memberType === null
                    ? '—'
                    : t(MEMBER_TYPE_LABEL_KEYS[detail.organization.memberType] ?? '')
                }
              />
              <Field
                label={t('admin.users.membershipStatus')}
                value={
                  detail.organization.membershipIsActive
                    ? t('admin.status.active')
                    : t('admin.status.inactive')
                }
              />
              <Field
                label={t('admin.users.joinedAt')}
                value={formatDateTime(detail.organization.joinedAt)}
              />
            </div>
            <div className={styles.actionRow}>
              <Link
                to={`/yonetim/isletmeler/${detail.organization.organizationId}`}
                className={styles.linkButton}
              >
                {t('admin.users.openOrganization')}
              </Link>
            </div>
          </>
        )}
      </Card>

      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.users.consentSection')}</h2>
        <div className={styles.detailGrid}>
          <Field
            label={t('admin.users.kvkk')}
            value={
              detail.consent.kvkkAccepted
                ? t('admin.users.consentAccepted', {
                    date: formatDateTime(detail.consent.kvkkAcceptedAt),
                  })
                : t('admin.users.consentPending')
            }
          />
          <Field
            label={t('admin.users.usageAgreement')}
            value={
              detail.consent.usageAgreementAccepted
                ? t('admin.users.consentAccepted', {
                    date: formatDateTime(detail.consent.usageAgreementAcceptedAt),
                  })
                : t('admin.users.consentPending')
            }
          />
          <Field
            label={t('admin.users.legalDocVersion')}
            value={detail.consent.legalDocVersion ?? '—'}
          />
          <Field
            label={t('admin.users.mustChangePassword')}
            value={detail.consent.mustChangePassword ? t('admin.yes') : t('admin.no')}
          />
        </div>
      </Card>

      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.users.actionsSection')}</h2>
        {isSelf ? (
          <p className={styles.warningNotice}>{t('admin.users.selfActionsBlocked')}</p>
        ) : (
          <p className={styles.notice}>{t('admin.users.actionsNotice')}</p>
        )}
        <div className={styles.actionRow}>
          <Button
            tone={user.isActive ? 'danger' : 'primary'}
            disabled={isSelf || busy}
            onClick={() => {
              setPending(user.isActive ? 'deactivate' : 'activate');
            }}
          >
            {user.isActive ? t('admin.users.deactivateAction') : t('admin.users.activateAction')}
          </Button>
          {/* YALNIZ DUSURME. Platform yoneticiligi VERITABANINDAN veriliyor (2026-08-30
              urun karari) ve sunucu da API'den vermeyi reddediyor: 403
              SUPER_ADMIN_NOT_ASSIGNABLE. Calismayacak bir "Yukselt" dugmesi cizmek
              kullaniciyi bos yere hataya surukler; dugme sadece super_admin'de gorunur. */}
          {isSuperAdmin ? (
            <Button
              tone="neutral"
              disabled={isSelf || busy}
              onClick={() => {
                setPending('demote');
              }}
            >
              {t('admin.users.demoteAction')}
            </Button>
          ) : null}
          <Button
            tone="neutral"
            iconLeft={<KeyRound size={16} aria-hidden="true" />}
            disabled={isSelf || busy}
            onClick={() => {
              setPending('passwordReset');
            }}
          >
            {t('admin.users.passwordResetAction')}
          </Button>
          <Button
            tone="ghost"
            onClick={() => {
              // Kimlik ROTA DURUMUNDA; adres cubuguna yazilmaz.
              void navigate('/yonetim/gecmis', { state: { actorUserId: user.id } });
            }}
          >
            {t('admin.users.viewAudit')}
          </Button>
        </div>
      </Card>

      <Dialog
        open={pending !== null}
        tone={pending === 'activate' ? 'primary' : 'danger'}
        title={copy.title}
        description={copy.description}
        confirmLabel={copy.confirmLabel}
        cancelLabel={t('common.cancel')}
        busy={busy}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={confirm}
      />
    </div>
  );
}

/** Etiket + deger ciftini tek yerden cizer; bos deger '—' olur. */
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value === '' ? '—' : value}</span>
    </div>
  );
}
